import { DndContext, MouseSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext } from "@dnd-kit/sortable";
import { useCallback, useMemo } from "react";

import type { InputMaybe, SubOptionItemInput } from "@/lib/gql/__generated__/graphql";

import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";
import { QuestionCardItemsWrapper } from "@/components/features/items/edit/_shared/ui/components/system/question/card/card-items-wrapper";

import { FormItem } from "@/components/ui/form/_base/form";

import { SortableSubOptionRow, SubOptionRow } from "./components";

import type { DragEndEvent } from "@dnd-kit/core";

type Props = {
	subOptions: ForArrayType<InputMaybe<SubOptionItemInput[]> | undefined> | undefined;

	appendSubOption: () => void;
	removeSubOption: (index: number) => void;

	onSetSubOptionName: (name: string, index: number) => void;
	onSetPriceAmount: (priceAmount: number | undefined, index: number) => void;
	onSetMaxDailyStockNum: (maxDailyStockNum: number | undefined, index: number) => void;
	onSetIsNextMorning: (isNextMorning: boolean, index: number) => void;
	onSetIsShowTimeSchedule: (props: {
		isShowTimeSchedule: boolean;
		index: number;
		startAt: string;
		endAt: string;
	}) => void;
	onSetStartAt: (startAt: string, index: number) => void;
	onSetEndAt: (endAt: string, index: number) => void;

	onUpdateOrderSubOptions: (subOptionIDs: string[]) => void;
};

export const QuestionOptionSubOptions = ({
	subOptions,
	appendSubOption,
	removeSubOption,
	onSetSubOptionName,
	onSetPriceAmount,
	onSetMaxDailyStockNum,
	onSetIsNextMorning,
	onSetIsShowTimeSchedule,
	onSetStartAt,
	onSetEndAt,
	onUpdateOrderSubOptions,
}: Props) => {
	const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 5 } });
	const sensors = useSensors(mouseSensor);

	const handleDragEnd = useCallback(
		({ active, over }: DragEndEvent) => {
			if (!subOptions?.value) return;
			if (!over || active.id === over.id) return;

			const activeIndex = subOptions.value.findIndex((item) => item.subOptionID?.value === active.id);
			const overIndex = subOptions.value.findIndex((item) => item.subOptionID?.value === over.id);
			if (activeIndex < 0 || overIndex < 0) return;

			const newSubOptions = [...subOptions.value];
			const [removed] = newSubOptions.splice(activeIndex, 1);
			newSubOptions.splice(overIndex, 0, removed);

			onUpdateOrderSubOptions(newSubOptions.map((item) => item.subOptionID?.value ?? ""));
		},
		[onUpdateOrderSubOptions, subOptions?.value],
	);

	const subOptionIDs = useMemo(() => {
		return subOptions?.value?.map((item) => item.subOptionID?.value ?? "") ?? [];
	}, [subOptions?.value]);

	const canReorder = (subOptions?.value?.length ?? 0) > 1;

	const sharedRowProps = useMemo(
		() => ({
			removeSubOption,
			onSetSubOptionName,
			onSetIsShowTimeSchedule,
			onSetStartAt,
			onSetEndAt,
			onSetIsNextMorning,
			onSetMaxDailyStockNum,
			onSetPriceAmount,
		}),
		[
			removeSubOption,
			onSetSubOptionName,
			onSetIsShowTimeSchedule,
			onSetStartAt,
			onSetEndAt,
			onSetIsNextMorning,
			onSetMaxDailyStockNum,
			onSetPriceAmount,
		],
	);

	return (
		<QuestionCardItemsWrapper appendItem={appendSubOption}>
			{canReorder ? (
				<>
					<p className="text-sm text-caption-50">
						左端の並べ替えアイコンをドラッグして、サブオプションの表示順を変更できます。
					</p>
					<DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
						<FormItem>
							<SortableContext items={subOptionIDs}>
								{subOptions?.value?.map((item, index) => {
									return item.subOptionID ? (
										<SortableSubOptionRow
											key={item.subOptionID.value}
											subOption={item}
											index={index}
											{...sharedRowProps}
										/>
									) : null;
								})}
							</SortableContext>
						</FormItem>
					</DndContext>
				</>
			) : (
				<FormItem>
					{subOptions?.value?.map((item, index) => {
						return item.subOptionID ? (
							<SubOptionRow
								key={item.subOptionID.value}
								subOption={item}
								index={index}
								{...sharedRowProps}
							/>
						) : null;
					})}
				</FormItem>
			)}
		</QuestionCardItemsWrapper>
	);
};
